import { type FC, useRef } from 'react'
import { AxiosError } from 'axios'
import type { FormikProps } from 'formik'
import { useTranslation } from 'react-i18next'

import { dataLayerPublish } from '@dhl/datalayer'
import { initialAddress } from '@dhl/input-address'
import {
  type ProfileAddress,
  usePostProfileAddresses,
  useDeleteProfileAddressesAddressid,
} from '@dhl/receiver-profile-api'
import { StackColumn, useSnackbar } from '@dhl-ui/components'

import { HomeIllustration } from '@dhl-ui/illustrations'
import { getTopLevelDomain } from '~/config/index.js'
import { useGetProfile, useGetProfileLastAddedAddress } from '~/profile/model/profile/hooks.js'
import { AddressForm, useCreateProfileAddressValidationSchema } from '~/profile/view/settings/addresses/AddressForm.js'
import { useWizardContext, useSetTitle } from '~/setup/model/context.js'
import { steps } from '~/setup/model/steps.js'
import { StepDescription } from '~/setup/view/components/StepDescription.js'
import { StepForm } from '~/setup/view/components/StepForm.js'

type FormValues = Omit<ProfileAddress, 'addressId'>

export const YourAddress: FC = () => {
  const { onStepComplete, currentStep, setStepSubmittedValues, getStepSubmittedValues } = useWizardContext()
  const { t } = useTranslation()

  useSetTitle(t('portal.profileSetup.yourAddress.pageTitle'))

  const { enqueueSnackbar } = useSnackbar()
  const { data: profile } = useGetProfile()
  const { data: lastAddedAddress } = useGetProfileLastAddedAddress({
    query: {
      // use the most up-to-date value of the last added address, since an address might have been added earlier in the wizard.
      staleTime: 0,
    },
  })
  const formRef = useRef<FormikProps<FormValues>>(null)
  const { mutateAsync: addProfileAddress } = usePostProfileAddresses()
  const { mutateAsync: deleteProfileAddress } = useDeleteProfileAddressesAddressid()

  const countryCode = getTopLevelDomain().toUpperCase()
  const previouslySubmittedValues = getStepSubmittedValues<FormValues>(steps.address)

  const initialAddressDetails = {
    firstName: profile?.firstName ?? '',
    lastName: profile?.lastName ?? '',
    email: profile?.email ?? '',
    mobileNumber: profile?.mobilePhone?.number ?? '',
    companyName: '',
    vatNumber: '',
  }

  const initialValues: FormValues = previouslySubmittedValues ?? {
    name: '',
    isDefaultShippingAddress: false,
    address: {
      ...initialAddress,
      countryCode,
    },
    addressDetails: initialAddressDetails,
  }

  const validationSchema = useCreateProfileAddressValidationSchema()

  const handleSubmit = async (values: FormValues) => {
    const existingAddressId = lastAddedAddress?.addressId
    let newAddressId: string | undefined

    try {
      if (currentStep.isPreviouslyCompleted && existingAddressId) {
        const hasChanged = JSON.stringify(values) !== JSON.stringify(previouslySubmittedValues)

        if (!hasChanged) {
          onStepComplete()
          return
        }

        await deleteProfileAddress({ addressId: existingAddressId })
      }

      const newAddress = await addProfileAddress({
        data: values,
      })

      newAddressId = newAddress?.addresses?.pop()?.addressId
    } catch (error: unknown) {
      const isAlreadyInUseError = error instanceof AxiosError && error.response?.status === 409

      const errorText = isAlreadyInUseError
        ? 'portal.profileSetup.yourAddress.alreadyExistsError'
        : 'common.pleaseTryAgainErrorMessage'

      if (!isAlreadyInUseError) {
        dataLayerPublish('ERROR', { error, page: 'your-address-wizard' })
      }

      enqueueSnackbar(t(errorText), { severity: 'error' })

      throw error
    }

    setStepSubmittedValues(steps.address, {
      ...values,
      addressId: newAddressId,
    })
    onStepComplete()
  }

  return (
    <StackColumn gap={3}>
      <StepDescription
        illustration={<HomeIllustration />}
        title={t('portal.profileSetup.yourAddress.stepTitle')}
        description={t('portal.profileSetup.yourAddress.stepDescription')}
      />

      <StepForm
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        innerRef={formRef}
        validateOnChange
        validateOnBlur
      >
        <AddressForm
          inputFields={{
            showCompanyName: false,
            showVatNumber: false,
          }}
          showDeliveryPreferences={false}
        />
      </StepForm>
    </StackColumn>
  )
}
